// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import styled from "styled-components/macro"
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Controller,
  useForm,
  useWatch
} from "react-hook-form"
import { createTagKeyword, deleteTagKeyword, updateTag } from "../actions"
import { getActivityType } from "../../activity-types/actions"
import * as TagsSlice from "../slice"
import * as ActivityTypesSlice from "../../activity-types/slice"
import Dialog, {
  ModalContentResetTop,
  DangerLabel,
  ModalCheckBox,
  ModalCheckBoxLabel,
  ModalInputText,
  ModalLabel,
  ModalTextArea,
  ModalSubmitWrapper,
  ModalSubmitButton,
} from "features/shared/Dialog"
import { Tag, TagKeyword } from "generated/legebill/type/tag_pb"
import EditTagDialogKeywords from "./EditTagDialogKeywords"
import { CreateTagKeywordRequest, DeleteTagKeywordRequest, TagUpdate, UpdateTagRequest } from "generated/legebill/site/tag/v4/tag_pb"
import { fieldMaskFor } from "lib/protoutils"
import { t } from "@lingui/macro"
import { Trans } from "@lingui/react"
import ActivityTypesDropdown from "../../activity-types/ActivityTypesDropdown"
import RemoveButton from "../../shared/RemoveButton"
import { TagActivity, TextEllipsis } from "../../shared/styles"
import { List } from "../../shared/search-dropdown/styles"
import { GetActivityTypeRequest } from "../../../generated/legebill/site/activitytype/v4/activity_type_pb"

const StyledActivityTypesDropdown = styled(ActivityTypesDropdown)`
  margin: 0 1rem;
  
  ${List} {
    min-height: 190px;
    max-height: 190px;
  }
`

const Separator = styled.div`
  height: 12px;
`

const ActivityTypeGroup = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
`

const ActivityTypeModalLabel = styled(ModalLabel)`
  margin-top: 1.3rem;
  margin-bottom: 1.3rem;
`

const ActivityTypeWrapper = styled.div`
  ${TagActivity};
  &:hover {
    & > ${RemoveButton} {
      display: inline-block;
    }
  }
`

const ActivityTypeLabel = styled.span`
  ${TextEllipsis};
  display: block;
`

export const KeywordsLabel = styled(ModalLabel)`
  margin-top: 0;
`

const MAX_LENGTH = 500
const maxLengthMessage = t({
  id: "msg_field_max_length_exceeded",
  message: "This field should be max {maxLength} characters.",
  values: { maxLength: MAX_LENGTH },
})

type FormValues = {
  name: string,
  description: string,
  keyword: string,
  keywords: string[],
  defaultActivityTypeId: string,
  allowedSuggestion: boolean,
}

const validationSchema = yup.object().shape({
  name: yup.string()
    .trim()
    .required(t({ id: "msg_error_field_required", message: "This field is required." }))
    .max(MAX_LENGTH, maxLengthMessage),
  keyword: yup.string()
    .trim()
    .when(["keywords"], (keywords, schema) => {
      // available length for the new keyword (count left from existing keywords)
      const availableLength = keywords ? (MAX_LENGTH - keywords.join(" ").length - 1) : MAX_LENGTH
      return schema.max(availableLength, maxLengthMessage)
    })
    .test("duplicatedKeyword",
      t({ id: "msg_error_keyword_exists", message: "This keyword already exists." }),
      (keyword, context): boolean => {
        return !context.parent.keywords || !context.parent.keywords.includes(keyword)
      })
    .nullable(),
})

type Props = {
  tag: Tag.AsObject,
  onClose: () => void,
}

const EditTagDialog = ({ tag, onClose }: Props): React.ReactElement<Props> => {
  const dispatch = useDispatch()
  const isLoadingTag = useSelector(TagsSlice.selectIsLoadingTag)
  const activityTypesCache = useSelector(ActivityTypesSlice.selectActivityTypesCache)

  const defaultValues = useMemo(() => ({
    name: tag.name,
    description: tag.description,
    keyword: "",
    keywords: tag.keywordsList.map(k => k.text),
    defaultActivityTypeId: tag.defaultActivityTypeId,
    allowedSuggestion: tag.suggestionConfig === Tag.SuggestionConfig.ELIGIBLE,
  }), [tag])

  const {
    register,
    control,
    setValue,
    formState: { isDirty, dirtyFields },
    handleSubmit,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(validationSchema),
    defaultValues,
  })
  const [watchDefaultActivityTypeId, watchKeywords] = useWatch({
    control,
    name: ["defaultActivityTypeId", "keywords"],
  })

  useEffect(() => {
    setValue("keywords", defaultValues.keywords)
  }, [defaultValues])

  const defaultActivityType = activityTypesCache[watchDefaultActivityTypeId]

  useEffect(() => {
    if (watchDefaultActivityTypeId === "") {
      return
    }
    if (defaultActivityType !== undefined) {
      return
    }
    dispatch(getActivityType(new GetActivityTypeRequest().setId(watchDefaultActivityTypeId)))
  }, [watchDefaultActivityTypeId, defaultActivityType])

  const submitForm = (formValues: FormValues): void => {
    if (!isDirty) {
      return
    }

    const tagUpdate = new TagUpdate()
      .setName(formValues.name)
      .setDescription(formValues.description)
      .setDefaultActivityTypeId(formValues.defaultActivityTypeId)
      .setSuggestionConfig(formValues.allowedSuggestion ? Tag.SuggestionConfig.ELIGIBLE : Tag.SuggestionConfig.DISALLOWED)

    // construct field mask for changed fields
    const fieldMask = fieldMaskFor<TagUpdate.AsObject>()
      .withField("name", dirtyFields["name"] === true)
      .withField("description", dirtyFields["description"] === true)
      .withField("suggestionConfig", dirtyFields["allowedSuggestion"] === true)
      .withField("defaultActivityTypeId", dirtyFields["defaultActivityTypeId"] === true)
      .build()

    dispatch(updateTag(new UpdateTagRequest()
      .setId(tag.id)
      .setTag(tagUpdate)
      .setUpdateMask(fieldMask)))
    // dialog will be closed from epic
    return
  }

  const addKeyword = (keyword: string) => {
    dispatch(createTagKeyword(new CreateTagKeywordRequest()
      .setKeyword(new TagKeyword().setTagId(tag.id).setText(keyword))))
  }

  const removeKeyword = (keyword: string) => {
    dispatch(deleteTagKeyword(new DeleteTagKeywordRequest()
      .setKeyword(new TagKeyword().setTagId(tag.id).setText(keyword))))
  }

  return (
    <Dialog
      title={t({ id: "label_edit_tag", message: "Edit Tag" })}
      loadingMessage={isLoadingTag ? t({ id: "msg_loading_edit_tag", message: "Fetching recent data" }) : undefined}
      onClose={onClose}
    >
      <ModalContentResetTop>
        <ModalLabel htmlFor="name">
          <Trans
            id="label_edit_tag_name"
            message="Name"
          />
        </ModalLabel>
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error }}) => (
            <>
              <ModalInputText
                id={field.name}
                onChange={field.onChange}
                onBlur={field.onChange}
                value={field.value}
                isError={error !== undefined}
                disabled={isLoadingTag}
              ></ModalInputText>
              {error && <DangerLabel>{error.message}</DangerLabel>}
            </>
          )}
        />
        <ModalLabel htmlFor="description">
          <Trans
            id="label_edit_tag_description"
            message="Description"
          />
        </ModalLabel>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <ModalTextArea
              id={field.name}
              rows={10}
              onChange={field.onChange}
              value={field.value}
              disabled={isLoadingTag}
            ></ModalTextArea>
          )}
        />
        <ActivityTypeGroup>
          <ActivityTypeModalLabel>
            <Trans
              id="label_default_activity"
              message="Default Activity"
            />
          </ActivityTypeModalLabel>
          <StyledActivityTypesDropdown
            addedItemIds={[watchDefaultActivityTypeId]}
            icon="add-activity-type"
            placement="bottom"
            showFavouriteButton={false}
            onDefaultActivityTypeSelected={(value) => setValue("defaultActivityTypeId", value, { shouldDirty: true })}
          />
          {defaultActivityType && (
            <ActivityTypeWrapper
              title={defaultActivityType.label}
              onClick={(event: React.MouseEvent<HTMLElement>): void => event.stopPropagation()}
            >
              <ActivityTypeLabel id="defaultActivityTypeId">{defaultActivityType.label}</ActivityTypeLabel>
              <RemoveButton onClick={() => setValue("defaultActivityTypeId", "", { shouldDirty: true })}/>
            </ActivityTypeWrapper>
          )}
        </ActivityTypeGroup>
        <KeywordsLabel htmlFor="keyword">
          <Trans
            id="label_edit_tag_keywords"
            message="Keywords"
          />
        </KeywordsLabel>
        <Controller
          name="keyword"
          control={control}
          render={({ field, fieldState: { error }}) => (
            <>
              <EditTagDialogKeywords
                id={field.name}
                keyword={field.value}
                keywords={watchKeywords}
                isErrored={error !== undefined}
                disabled={isLoadingTag}
                onChangeKeyword={field.onChange}
                onAddKeyword={addKeyword}
                onRemoveKeyword={removeKeyword}
              />
              {error && <DangerLabel>{error.message}</DangerLabel>}
            </>
          )}
        />
        <Separator />
        <ModalCheckBoxLabel>
          <ModalCheckBox
            {...register("allowedSuggestion")}
            type="checkbox"
            disabled={isLoadingTag}
          />
          <Trans
            id="label_edit_tag_include_for_tag_suggestions"
            message="Include for tag suggestions"
          />
        </ModalCheckBoxLabel>
      </ModalContentResetTop>
      <ModalSubmitWrapper>
        <ModalSubmitButton
          type="button"
          disabled={isLoadingTag}
          onClick={handleSubmit(submitForm)}
        >
          <Trans
            id="label_save"
            message="Save"
          />
        </ModalSubmitButton>
      </ModalSubmitWrapper>
    </Dialog>
  )
}

export default EditTagDialog
